import { ChevronRight, Close, KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'src/app/hooks';
import useEcommerce from 'src/services/useEcommerce';
import useQueryUrl from 'src/hooks/useQuery';
import { useNavigate } from 'react-router';
import {
  IEcommerceSubBrandHierarchy,
  IGroupProductEcommerce
} from 'src/models/ecommerce';
import TableBrand from './TableBrand';
import TableSubBrand from './TableSubbrand';
import { colorsComparisonHex } from 'src/assets/js/colors3';
import { useAlert } from 'src/hooks/useAlert';
import { ECOMMERCE_COMPARISON } from 'src/route';
import {
  ECOMMERCE_GET_COMPARISON_ADVANCE_METRIC,
  ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_RATING,
  ECOMMERCE_GET_COMPARISON_LIST,
  ECOMMERCE_GET_COMPARISON_POPULARITY,
  ECOMMERCE_GET_COMPARISON_POSITIONING,
  ECOMMERCE_GET_COMPARISON_SENTIMENT
} from 'src/services/graphql/ecommerce/query';
import { IGroupObject } from 'src/models/objectManagement';
import TableGroup from './TableGroup';

export interface IGroupWithColor extends IGroupObject {
  color: string;
  isInputColor: boolean;
}

export interface IGroupProductEcommerceWithColor
  extends IGroupProductEcommerce {
  color: string;
  isInputColor: boolean;
}

export interface IEcommerceSubBrandHierarchyWithColor
  extends IEcommerceSubBrandHierarchy {
  color: string;
  isInputColor: boolean;
}

export default function EditItemComparison() {
  const theme = useTheme();
  const projectId = useAppSelector((state) => state.users.project.id);
  const [open, setOpen] = useState<boolean>(false);
  const [dataGroup, setDataGroup] = useState<IGroupWithColor[]>([]);
  const [dataBrand, setDataBrand] = useState<IGroupProductEcommerceWithColor[]>(
    []
  );
  const [dataSubBrand, setDataSubBrand] = useState<
    IEcommerceSubBrandHierarchyWithColor[]
  >([]);
  const [selectedGroup, setSelectedGroup] = useState<IGroupWithColor[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<
    IGroupProductEcommerceWithColor[]
  >([]);
  const [selectedSubBrands, setSelectedSubBrands] = useState<
    IEcommerceSubBrandHierarchyWithColor[]
  >([]);
  const query = useQueryUrl();
  const comparisonId = query.get('id');
  const sourceType = query.get('sourceType');

  const { handleClickAlert } = useAlert();
  const navigate = useNavigate();

  const {
    useGetEcommerceComparisonById,
    useGetEcommerceProductGroups,
    useEditEcommerceComparison,
    useGetEcommerceGroups
  } = useEcommerce();

  const [editComparisonEcommerce, resEditComparisonEcommerce] =
    useEditEcommerceComparison({
      refetchQueries: [
        ECOMMERCE_GET_COMPARISON_LIST,
        ECOMMERCE_GET_COMPARISON_POSITIONING,
        ECOMMERCE_GET_COMPARISON_POPULARITY,
        ECOMMERCE_GET_COMPARISON_SENTIMENT,
        ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_RATING,
        ECOMMERCE_GET_COMPARISON_ADVANCE_METRIC
      ]
    });

  const {
    data: ecommerceComparisonEntity,
    loading: loadingEcommerceComparisonEntity,
    refetch: getEcommerceComparisonEntity
  } = useGetEcommerceComparisonById({
    variables: {
      comparisonId
    },
    skip: !comparisonId,
    fetchPolicy: 'network-only'
  });

  const { data: groupProductData, loading: loadingGroupProductData } =
    useGetEcommerceProductGroups({
      variables: {
        projectId: projectId,
        depth: 'brandWithSubBrand'
      },
      skip: sourceType !== 'productGroup',
      fetchPolicy: 'network-only'
    });

  const { data: groupEcommerce, loading: loadingGroupEcommerce } =
    useGetEcommerceGroups({
      variables: {
        projectId,
        templateName: 'content',
        objectGroupType: 'ecommerce'
      },
      skip: sourceType !== 'objectGroup',
      fetchPolicy: 'network-only'
    });

  const currentComparisonNames = useMemo(() => {
    if (!ecommerceComparisonEntity || loadingEcommerceComparisonEntity)
      return [];
    return ecommerceComparisonEntity.ecommerceComparison_getComparisonEntity.groups.map(
      (item) => item.displayName
    );
  }, [ecommerceComparisonEntity, loadingEcommerceComparisonEntity]);

  const currentComparisonColors = useMemo(() => {
    if (!ecommerceComparisonEntity || loadingEcommerceComparisonEntity)
      return [];
    return ecommerceComparisonEntity.ecommerceComparison_getComparisonEntity
      .colors;
  }, [ecommerceComparisonEntity, loadingEcommerceComparisonEntity]);

  const colorHex = (index: number) => {
    if (index > 19) return colorsComparisonHex[index % 20];
    return colorsComparisonHex[index];
  };

  const groupList: IGroupWithColor[] = useMemo(() => {
    if (
      !groupEcommerce ||
      loadingGroupEcommerce ||
      !ecommerceComparisonEntity ||
      sourceType !== 'objectGroup'
    )
      return [];
    return groupEcommerce.objectManagement_getObjectGroupList.map(
      (item, index) => {
        const findIndexColor =
          ecommerceComparisonEntity.ecommerceComparison_getComparisonEntity.groups.findIndex(
            (group) => group.objectGroup.id === item.id
          );
        return {
          ...item,
          color:
            findIndexColor > -1
              ? currentComparisonColors[findIndexColor]
              : colorHex(index),
          isInputColor: false
        };
      }
    );
  }, [
    groupEcommerce,
    currentComparisonColors,
    colorHex,
    ecommerceComparisonEntity,
    colorsComparisonHex,
    loadingGroupEcommerce
  ]);

  const brandList: IGroupProductEcommerceWithColor[] = useMemo(() => {
    if (
      !groupProductData ||
      loadingEcommerceComparisonEntity ||
      !ecommerceComparisonEntity ||
      sourceType !== 'productGroup'
    )
      return [];
    return groupProductData.ecommerceProductManagement_getGroupProduct.map(
      (item, index) => {
        const findIndexColor =
          ecommerceComparisonEntity.ecommerceComparison_getComparisonEntity.groups.findIndex(
            (group) => group.productGroup.id === item.id
          );
        return {
          ...item,
          color:
            findIndexColor > -1
              ? currentComparisonColors[findIndexColor]
              : colorHex(index),
          isInputColor: false
        };
      }
    );
  }, [
    groupProductData,
    currentComparisonColors,
    colorHex,
    ecommerceComparisonEntity,
    colorsComparisonHex,
    sourceType,
    loadingEcommerceComparisonEntity
  ]);

  const subbrandList: IEcommerceSubBrandHierarchyWithColor[] = useMemo(() => {
    if (
      !groupProductData ||
      loadingEcommerceComparisonEntity ||
      !ecommerceComparisonEntity ||
      sourceType !== 'productGroup'
    )
      return [];
    return groupProductData.ecommerceProductManagement_getGroupProduct.flatMap(
      (item) =>
        item.subBrands.map((value, index) => {
          const findIndexColor =
            ecommerceComparisonEntity.ecommerceComparison_getComparisonEntity.groups.findIndex(
              (group) => group.productGroup.id === value.id
            );
          return {
            ...value,
            color:
              findIndexColor > -1
                ? currentComparisonColors[findIndexColor]
                : colorHex(index),
            isInputColor: false
          };
        })
    );
  }, [
    groupProductData,
    currentComparisonColors,
    colorHex,
    ecommerceComparisonEntity,
    colorsComparisonHex,
    sourceType,
    loadingEcommerceComparisonEntity
  ]);

  const mergedBrandSubbrand = useMemo(() => {
    return [
      ...selectedBrands.map((brand) => ({
        id: brand.id,
        name: brand.name,
        color: brand.color
      })),
      ...selectedSubBrands.map((subbrand) => ({
        id: subbrand.id,
        name: subbrand.name,
        color: subbrand.color
      }))
    ];
  }, [selectedBrands, selectedSubBrands]);

  const finalComparisonItems = useMemo(() => {
    if (sourceType === 'productGroup') return mergedBrandSubbrand;
    return selectedGroup.map((group) => ({
      id: group.id,
      name: group.displayName,
      color: group.color
    }));
  }, [sourceType, mergedBrandSubbrand, selectedGroup]);

  const handleOpen = () => {
    if (sourceType === 'productGroup' || sourceType === 'objectGroup') {
      setOpen(true);
    } else {
      navigate(
        `${ECOMMERCE_COMPARISON}?tab=form-edit&id=${comparisonId}&sourceType=${sourceType}&fromDashboard=true`
      );
    }
  };

  const setInitialComparisonGroup = () => {
    if (
      !groupEcommerce ||
      loadingEcommerceComparisonEntity ||
      loadingGroupEcommerce ||
      !ecommerceComparisonEntity ||
      sourceType !== 'objectGroup'
    )
      return;
    const filterSelectedObject = groupList.filter((group) =>
      ecommerceComparisonEntity.ecommerceComparison_getComparisonEntity.groups.some(
        (item) => item.objectGroup.id === group.id
      )
    );
    setSelectedGroup(filterSelectedObject);
    setDataGroup(groupList);
  };

  const setInitialComparisonBrand = () => {
    if (
      !ecommerceComparisonEntity ||
      loadingEcommerceComparisonEntity ||
      loadingGroupProductData ||
      !groupProductData ||
      sourceType !== 'productGroup'
    )
      return;
    const filterSelectedBrand = brandList.filter((brand) =>
      ecommerceComparisonEntity.ecommerceComparison_getComparisonEntity.groups.some(
        (item) => brand.id === item.productGroup.id
      )
    );
    const filterSelectedSubBrand = subbrandList.filter((brand) =>
      ecommerceComparisonEntity.ecommerceComparison_getComparisonEntity.groups.some(
        (item) => brand.id === item.productGroup.id
      )
    );
    setSelectedBrands(filterSelectedBrand);
    setSelectedSubBrands(filterSelectedSubBrand);
    setDataBrand(brandList);
    setDataSubBrand(subbrandList);
  };

  const handleClose = () => {
    setInitialComparisonGroup();
    setInitialComparisonBrand();
    setOpen(false);
  };

  const handleApply = () => {
    const groupPayload = finalComparisonItems.map((item) => {
      const result = {
        name: item.name,
        objectList: undefined,
        objectGroup: sourceType === 'objectGroup' ? item.id : undefined,
        labelList: undefined,
        productGroupId: sourceType === 'productGroup' ? item.id : undefined,
        color: item.color
      };
      return result;
    });

    const payload = {
      projectId,
      comparisonId,
      comparisonName:
        ecommerceComparisonEntity.ecommerceComparison_getComparisonEntity
          .displayName,
      comparisonGroups: groupPayload,
      sourceType
    };
    editComparisonEcommerce({
      variables: {
        comparisonName: payload.comparisonName,
        comparisonId: payload.comparisonId,
        comparisonGroups: payload.comparisonGroups
      }
    }).then(({ data }) => {
      if (!data) return;
      const editedComparisonRes = data.ecommerceComparison_editComparison;
      getEcommerceComparisonEntity({
        comparisonId: editedComparisonRes.id
      });
      handleClickAlert({
        horizontal: 'center',
        vertical: 'top',
        message: 'Edit comparison success!',
        severity: 'success'
      });
      navigate(
        `${ECOMMERCE_COMPARISON}?tab=dashboard&title=${editedComparisonRes.displayName}&id=${editedComparisonRes.id}&sourceType=${sourceType}`
      );
      handleClose();
    });
  };

  const dialogText = useMemo(() => {
    if (sourceType === 'productGroup') return 'Brand or Sub-brand';
    if (sourceType === 'object') return 'Object';
    if (sourceType === 'label') return 'Label';
    return 'Group';
  }, [sourceType]);

  useEffect(() => {
    setInitialComparisonGroup();
  }, [
    ecommerceComparisonEntity,
    groupEcommerce,
    sourceType,
    loadingEcommerceComparisonEntity,
    loadingGroupEcommerce
  ]);

  useEffect(() => {
    setInitialComparisonBrand();
  }, [
    ecommerceComparisonEntity,
    groupProductData,
    sourceType,
    loadingEcommerceComparisonEntity,
    loadingGroupProductData
  ]);

  return (
    <>
      <Stack width="100%" direction="row" alignItems="center" spacing={1}>
        <Typography variant="h5" fontWeight={600}>
          Select {dialogText}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleOpen}
          sx={{
            color: 'text.primary',
            backgroundColor: 'white',
            borderRadius: theme.spacing(1),
            maxWidth: '400px',
            width: '100%',
            minHeight: '36px',
            borderColor: (theme) =>
              open ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)',
              backgroundColor: 'white'
            },
            '& .MuiButton-startIcon': { marginRight: 'auto' },
            '& .buttonText': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              textAlign: 'left',
              flex: '1'
            },
            padding: '4px 8px'
          }}
          endIcon={
            <KeyboardArrowDown
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0)',
                marginRight: '2px'
              }}
            />
          }
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 0.5,
              // maxWidth: '320px',
              // maxWidth: '1000px',
              width: '100%',
              overflow: 'scroll',
              scrollbarWidth: 'none',
              alignItems: 'center'
            }}
          >
            {currentComparisonNames.map((brand, index) => (
              <>
                <Chip
                  variant="outlined"
                  key={index}
                  sx={{
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block'
                    }
                  }}
                  label={brand}
                  size="small"
                />
                {index !== currentComparisonNames.length - 1 && (
                  <Typography variant="h5" fontWeight={600}>
                    vs
                  </Typography>
                )}
              </>
            ))}
          </Box>
        </Button>
      </Stack>
      {open && (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
          <Box
            sx={{
              py: theme.spacing(2),
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              px={2}
            >
              <Stack spacing={1} width="75%">
                <Typography fontSize="16px" fontWeight={700}>
                  Select {dialogText} to Compare
                </Typography>
                <Typography fontSize="14px" color={theme.palette.grey[500]}>
                  Choose {dialogText.toLowerCase()} items, then assign colors
                  for each item. These colors will be used in the comparison
                  chart and dashboard visualization.
                </Typography>
              </Stack>
              <Stack alignItems="end">
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    color: theme.palette.grey[500]
                  }}
                >
                  <Close />
                </IconButton>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: theme.spacing(1),
                    p: theme.spacing(1),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    backgroundColor: theme.palette.grey[100]
                  }}
                >
                  <Typography fontSize="14px" color={theme.palette.grey[500]}>
                    Selected Items
                  </Typography>
                  <Typography fontSize="16px" fontWeight={700}>
                    {finalComparisonItems.length}
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            <Box
              sx={{
                backgroundColor: theme.palette.grey[100],
                p: theme.spacing(2),
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                width: '100%',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  border: `1px solid ${theme.palette.grey[300]}`,
                  p: theme.spacing(1),
                  borderRadius: theme.spacing(1),
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  width: '100%',
                  backgroundColor: 'white',
                  height: '70px'
                }}
              >
                <Typography
                  fontSize="14px"
                  color={theme.palette.grey[500]}
                  fontWeight={500}
                >
                  Current Comparison
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: 0.5,
                    width: '100%',
                    overflow: 'scroll',
                    scrollbarWidth: 'none',
                    alignItems: 'center',
                    maxHeight: '300px',
                    maxWidth: '400px'
                  }}
                >
                  {currentComparisonNames.map((brand, index) => (
                    <>
                      <Chip
                        variant="outlined"
                        key={index}
                        sx={{
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block'
                          }
                        }}
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Box
                              sx={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: currentComparisonColors[index],
                                borderRadius: '100%'
                              }}
                            />
                            <Typography fontSize="14px">{brand}</Typography>
                          </Stack>
                        }
                        size="small"
                      />
                    </>
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.grey[500]}`,
                  borderRadius: '100%',
                  backgroundColor: theme.colors.primary.dark,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: theme.spacing(0.5)
                }}
              >
                <ChevronRight htmlColor="#FFF" />
              </Box>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.grey[300]}`,
                  p: theme.spacing(1),
                  borderRadius: theme.spacing(1),
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  width: '100%',
                  backgroundColor: 'white',
                  height: '70px'
                }}
              >
                <Typography
                  fontSize="14px"
                  color={theme.palette.grey[500]}
                  fontWeight={500}
                >
                  New Comparison Preview
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: 0.5,
                    width: '100%',
                    overflow: 'scroll',
                    scrollbarWidth: 'none',
                    alignItems: 'center',
                    maxHeight: '300px',
                    maxWidth: '400px'
                  }}
                >
                  {finalComparisonItems.map((brand, index) => (
                    <>
                      <Chip
                        variant="outlined"
                        key={index}
                        sx={{
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block'
                          }
                        }}
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Box
                              sx={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: brand.color,
                                borderRadius: '100%'
                              }}
                            />
                            <Typography fontSize="14px">
                              {brand.name}
                            </Typography>
                          </Stack>
                        }
                        size="small"
                      />
                    </>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                maxHeight: '400px',
                display: 'flex',
                gap: theme.spacing(3),
                px: theme.spacing(2)
              }}
            >
              {sourceType === 'productGroup' ? (
                <>
                  <Stack spacing={1} width="100%">
                    <TableBrand
                      dataBrand={dataBrand}
                      setDataBrand={setDataBrand}
                      selectedBrands={selectedBrands}
                      setSelectedBrands={setSelectedBrands}
                    />
                  </Stack>
                  <Stack spacing={1} width="100%">
                    <TableSubBrand
                      dataSubBrand={dataSubBrand}
                      setDataSubBrand={setDataSubBrand}
                      selectedSubBrands={selectedSubBrands}
                      setSelectedSubBrands={setSelectedSubBrands}
                    />
                  </Stack>
                </>
              ) : (
                <>
                  <Stack spacing={1} width="100%">
                    <TableGroup
                      dataGroup={dataGroup}
                      setDataGroup={setDataGroup}
                      selectedGroup={selectedGroup}
                      setSelectedGroup={setSelectedGroup}
                    />
                  </Stack>
                </>
              )}
            </Box>
            <Stack pt={2} px={2} alignItems="end">
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={
                  finalComparisonItems.length < 2 ||
                  resEditComparisonEcommerce.loading
                }
              >
                {resEditComparisonEcommerce.loading ? 'Loading...' : 'Apply'}
              </Button>
            </Stack>
          </Box>
        </Dialog>
      )}
    </>
  );
}
